import { useState, type CSSProperties } from 'react';
import { CircleAlert, Info } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
  useDashboardDataTableState,
  type DashboardDataTableFooterRow,
  type DashboardDataTableSortState,
} from '@shigo-ui/dashboard-ui';
import { Alert, AlertDescription } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { reportMetricDefinitions } from '../../catalog';
import { formatReportMetricValue, numericReportValue, reportMetricLabel } from '../../format';
import type { ReportRequest, ReportResponse } from '../../model';
import { reportPageSizes } from '../../model';
import type { ReportMetric } from '../../reportEnums';
import { ReportMetricFormat } from '../../reportEnums';
import { displayMetricsForDimensions, reportDisplayMetrics } from '../../reportRequest';

interface ReportResultsTableProps {
  request: ReportRequest;
  response?: ReportResponse | undefined;
  loading: boolean;
  error: boolean;
  /** No table report has been run yet. */
  notRun?: boolean;
}

interface ReportResultRow {
  key: string;
  values: ReportResponse['report'][number];
}

export function ReportResultsTable({
  request,
  response,
  loading,
  error,
  notRun = false,
}: ReportResultsTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const metrics = reportDisplayMetrics(request);
  const rows: ReportResultRow[] = (response?.report ?? []).map((values, index) => ({
    key: `report-row-${String(index)}`,
    values,
  }));
  const filteredRows = rows.filter((row) => rowMatchesSearch(row, metrics, searchTerm));
  const table = useDashboardDataTableState({
    initialPageSize: 25,
    totalItems: filteredRows.length,
    pageSizes: reportPageSizes,
    disabled: loading,
  });
  const { page, pageSize, sort, resetPage } = table;
  const search = useDebouncedValue(
    searchTerm,
    (value) => {
      setSearchTerm(value);
      resetPage();
    },
    650,
  );
  const sorted = sort
    ? [...filteredRows].sort((left, right) => compareRows(left, right, sort))
    : filteredRows;
  const visibleRows = sorted.slice(page * pageSize, page * pageSize + pageSize);
  const identityMetrics = new Set(displayMetricsForDimensions(request.dimensions));
  const columns = buildDashboardDataTableColumns<ReportResultRow>(
    metrics.map((metric) => ({
      id: metric,
      header: (
        <span className="line-clamp-2 min-w-0 flex-1 text-left leading-4 break-words whitespace-normal">
          {reportMetricLabel(metric)}
        </span>
      ),
      cell: (row: ReportResultRow) => {
        const value = formatReportMetricValue(metricValue(row, metric), metric);
        return (
          <span className="block truncate" title={value}>
            {value}
          </span>
        );
      },
      kind: DashboardDataTableColumnKind.Custom,
      sortable: true,
      sortKey: metric,
      className: 'w-40 min-w-40 max-w-40 overflow-hidden',
      headerClassName:
        'w-40 min-w-40 max-w-40 overflow-hidden whitespace-normal [&>button]:h-auto [&>button]:min-h-8 [&>button]:w-full [&>button]:min-w-0 [&>button]:justify-start [&>button]:overflow-hidden [&>button]:whitespace-normal',
    })),
  );
  const footerRows: DashboardDataTableFooterRow<ReportResultRow>[] | undefined = response?.total
    ? [
        {
          id: 'report-total',
          cell: (column) => {
            const metric = column.id as ReportMetric;
            if (identityMetrics.has(metric)) {
              return metric === metrics[0] ? dashboardT('reportExplorer.table.total') : '';
            }
            return reportMetricDefinitions[metric].format === ReportMetricFormat.String
              ? ''
              : formatReportMetricValue(
                  response.total?.[reportMetricDefinitions[metric].field],
                  metric,
                );
          },
          className: 'font-semibold',
        },
      ]
    : undefined;

  const layoutStyle = {
    '--report-table-min-width': `${String(Math.max(metrics.length, 1) * 160)}px`,
  } as CSSProperties;

  return (
    <div className="flex min-w-0 flex-1 flex-col" style={layoutStyle}>
      <DashboardDataTable
        fillEmptyHeight
        className="max-w-none gap-2"
        tableLabel={dashboardT('reportExplorer.table.label')}
        columns={columns}
        items={visibleRows}
        getRowKey={(row) => row.key}
        loading={loading}
        errorMessage={error ? dashboardT('reportExplorer.table.error') : undefined}
        loadingMessage={dashboardT('reportExplorer.table.loading')}
        filterToolbar={
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
            <h2 className="text-base font-semibold">{dashboardT('reportExplorer.table.title')}</h2>
            {response && !loading ? (
              <span className="text-muted-foreground text-xs">
                {dashboardT('common.item', { count: filteredRows.length })}
              </span>
            ) : null}
            <HighCardinalityNotice response={response} />
            <DashboardTableSearchInput
              containerClassName="ml-auto sm:w-[360px]"
              className="h-8"
              value={search.draft}
              aria-label={dashboardT('reportExplorer.table.search')}
              placeholder={dashboardT('reportExplorer.table.search')}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
          </div>
        }
        emptyMessage={dashboardT(emptyMessageKey(notRun, searchTerm))}
        minWidthClassName="min-w-(--report-table-min-width)"
        pagination={table.pagination}
        sort={table.sortProps}
        footerRows={footerRows}
      />
    </div>
  );
}

function emptyMessageKey(notRun: boolean, searchTerm: string) {
  if (notRun) return 'reportExplorer.table.notRun';
  return searchTerm.trim() ? 'reportExplorer.table.searchEmpty' : 'reportExplorer.table.empty';
}

function HighCardinalityNotice({ response }: { response: ReportResponse | undefined }) {
  const meta = response?.highCardinalityReportMeta;
  if (!meta?.limited) return null;

  const limitedRows = meta.limitedRows ?? response?.report.length ?? 0;
  const exportFailed = Boolean(meta.errorMessage?.trim()) || !meta.downloadUrl?.trim();
  let message = dashboardT('reportExplorer.highCardinality.limited', {
    count: limitedRows,
    limitedRows,
  });
  if (
    !exportFailed &&
    meta.downloadUrl?.trim() &&
    meta.exportedRows !== null &&
    meta.exportedRows !== undefined
  ) {
    message = dashboardT('reportExplorer.highCardinality.exportReady', {
      limitedRows,
      exportedRows: meta.exportedRows,
      count: limitedRows,
    });
  } else if (exportFailed) {
    message = dashboardT('reportExplorer.highCardinality.exportError', {
      count: limitedRows,
      limitedRows,
    });
  }

  return (
    <Alert
      role={exportFailed ? 'alert' : 'status'}
      variant={exportFailed ? 'destructive' : 'default'}
      className="flex h-9 flex-1 items-center py-0 [&>svg]:top-1/2 [&>svg]:-translate-y-1/2 [&>svg+div]:translate-y-0"
    >
      {exportFailed ? (
        <CircleAlert className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Info className="h-4 w-4" aria-hidden="true" />
      )}
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

function rowMatchesSearch(
  row: ReportResultRow,
  metrics: readonly ReportMetric[],
  searchTerm: string,
) {
  const query = searchTerm.trim().toLocaleLowerCase();
  if (!query) return true;

  return metrics.some((metric) => {
    const value = metricValue(row, metric);
    const rawValue = value === null || value === undefined ? '' : String(value);
    const formattedValue = formatReportMetricValue(value, metric);
    return [rawValue, formattedValue].some((candidate) =>
      candidate.toLocaleLowerCase().includes(query),
    );
  });
}

function compareRows(
  left: ReportResultRow,
  right: ReportResultRow,
  sort: DashboardDataTableSortState,
) {
  const metric = sort.key as ReportMetric;
  const leftValue = metricValue(left, metric);
  const rightValue = metricValue(right, metric);
  const leftNumber = numericReportValue(leftValue);
  const rightNumber = numericReportValue(rightValue);
  const comparison =
    leftNumber !== undefined && rightNumber !== undefined
      ? leftNumber - rightNumber
      : String(leftValue ?? '').localeCompare(String(rightValue ?? ''));
  return sort.direction === DashboardDataTableSortDirection.Asc ? comparison : -comparison;
}

function metricValue(row: ReportResultRow, metric: ReportMetric) {
  return row.values[reportMetricDefinitions[metric].field];
}
